/**
 * One z plane per node type, solved in-plane by the same force layout.
 *
 * Closer to how an architect draws a landscape than any free layout is: drawing each type on its
 * own plane makes the direction of every dependency readable from its slope alone. The cost is that
 * clustering is only expressible within a plane - two applications that belong together can sit
 * next to each other, but an application and the technology under it cannot.
 *
 * The plane *order* comes from `toTypeLayering`, which derives it from the references. It is not
 * by hierarchy depth: depth would put a capability and an application on the same plane whenever
 * the model nests them equally deep, which is common and which destroys the one thing the layout
 * is for.
 */

import type { Vec3 } from '../types';

/**
 * A grid on each plane.
 *
 * Without it the planes are invisible and the view is a free layout that happens to be stratified -
 * a viewer has no way to tell that the z gaps are equal and meaningful rather than emergent. The
 * grids share one extent so the planes read as a stack rather than as unrelated sheets.
 */
export const toLayerGuides = (
  positions: readonly Vec3[],
  layerIndices: readonly number[],
  divisions: number,
  layerSpacing: number
): Vec3[][] => {
  if (positions.length === 0) return [];

  const used = new Set(layerIndices);

  /* Sized to a high quantile rather than to the maximum. A force layout leaves a handful of
     weakly-connected nodes far outside the body of a layer, and a grid drawn to reach them is
     several times wider than the data it is supposed to be a floor under - which after the uniform
     cube fit squashes the whole stack into a thin sliver in the middle of huge sheets. */
  const distances = positions
    .map(([x, y]) => Math.max(Math.abs(x), Math.abs(y)))
    .sort((left, right) => left - right);
  const quantile =
    distances[Math.floor(distances.length * 0.92)] ??
    distances[distances.length - 1] ??
    1;

  const half = Math.max(1e-3, quantile) * 1.12;
  const step = (half * 2) / divisions;

  return [...used].flatMap(layer => {
    const z = layer * layerSpacing;

    return Array.from({ length: divisions + 1 }, (_, index) => {
      const at = -half + index * step;
      return [
        [
          [-half, at, z],
          [half, at, z],
        ] as Vec3[],
        [
          [at, -half, z],
          [at, half, z],
        ] as Vec3[],
      ];
    }).flat();
  });
};
